import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Stack,
  Chip,
  Avatar,
  LinearProgress,
  Alert
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Pets as PetsIcon,
  People as PeopleIcon,
  PhotoCamera as PhotoIcon,
  Upload as UploadIcon,
  Assessment as StatsIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { shelterService, ShelterAnimal } from '@services/sheltersService';

interface ShelterStats {
  totalAnimals: number;
  availableAnimals: number;
  adoptedAnimals: number;
  totalStaff: number;
  pendingApplications: number;
  recentAdoptions: number;
}

const ShelterDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState<ShelterStats>({
    totalAnimals: 0,
    availableAnimals: 0,
    adoptedAnimals: 0,
    totalStaff: 0,
    pendingApplications: 0,
    recentAdoptions: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      if (!user?.shelterId) {
        throw new Error('Shelter ID not found');
      }

      // Get shelter statistics
      const [animalsResponse, staffResponse] = await Promise.all([
        shelterService.getShelterAnimals(user.shelterId, { page: 1, limit: 1 }),
        shelterService.getShelterStaff(user.shelterId)
      ]);

      const totalAnimals = animalsResponse.total || 0;
      const availableAnimals = animalsResponse.animals?.filter((animal: ShelterAnimal) => 
        animal.status === 'available'
      ).length || 0;
      const adoptedAnimals = animalsResponse.animals?.filter((animal: ShelterAnimal) => 
        animal.status === 'adopted'
      ).length || 0;
      const totalStaff = staffResponse.length || 0;

      setStats({
        totalAnimals,
        availableAnimals,
        adoptedAnimals,
        totalStaff,
        pendingApplications: 0, // TODO: Implement applications system
        recentAdoptions: 0 // TODO: Implement recent adoptions tracking
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Set default values on error
      setStats({
        totalAnimals: 0,
        availableAnimals: 0,
        adoptedAnimals: 0,
        totalStaff: 0,
        pendingApplications: 0,
        recentAdoptions: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ 
    title, 
    value, 
    icon, 
    color = 'primary',
    subtitle,
    onClick 
  }: {
    title: string;
    value: number;
    icon: React.ReactNode;
    color?: 'primary' | 'success' | 'warning' | 'info';
    subtitle?: string;
    onClick?: () => void;
  }) => (
    <Card 
      sx={{ 
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: 4
        } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: `${color}.main`, width: 48, height: 48 }}>
            {icon}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" fontWeight="bold" color={`${color}.main`}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  const QuickActionCard = ({ 
    title, 
    description, 
    icon, 
    buttonText, 
    onClick,
    color = 'primary'
  }: {
    title: string;
    description: string;
    icon: React.ReactNode;
    buttonText: string;
    onClick: () => void;
    color?: 'primary' | 'success' | 'warning' | 'info';
  }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <Avatar sx={{ bgcolor: `${color}.light`, color: `${color}.main` }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
      <CardActions>
        <Button 
          variant="contained" 
          color={color}
          startIcon={icon}
          onClick={onClick}
          fullWidth
        >
          {buttonText}
        </Button>
      </CardActions>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Dashboard Adapost
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Bun venit înapoi! Iată o privire de ansamblu asupra activității adapostului.
        </Typography>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Animale"
            value={stats.totalAnimals}
            icon={<PetsIcon />}
            color="primary"
            subtitle={`${stats.availableAnimals} disponibile`}
            onClick={() => navigate('/shelter/animals')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Adoptate"
            value={stats.adoptedAnimals}
            icon={<TrendingUpIcon />}
            color="success"
            subtitle="luna aceasta"
            onClick={() => navigate('/shelter/animals?status=adopted')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Cereri în Așteptare"
            value={stats.pendingApplications}
            icon={<ScheduleIcon />}
            color="warning"
            subtitle="necesită atenție"
            onClick={() => navigate('/shelter/applications')}
          />
        </Grid>
        {user?.role === 'shelter_admin' && (
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Echipa"
              value={stats.totalStaff}
              icon={<PeopleIcon />}
              color="info"
              subtitle="membri activi"
              onClick={() => navigate('/shelter/staff')}
            />
          </Grid>
        )}
      </Grid>

      {/* Quick Actions */}
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Acțiuni Rapide
      </Typography>
      
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <QuickActionCard
            title="Adaugă Animal Nou"
            description="Înregistrează un animal nou în sistem"
            icon={<AddIcon />}
            buttonText="Adaugă Animal"
            color="primary"
            onClick={() => navigate('/shelter/animals/new')}
          />
        </Grid>
        {user?.role === 'shelter_admin' && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <QuickActionCard
                title="Import în Masă"
                description="Importă animale din fișier CSV/Excel"
                icon={<UploadIcon />}
                buttonText="Import CSV"
                color="success"
                onClick={() => navigate('/shelter/import')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <QuickActionCard
                title="Gestionează Staff"
                description="Adaugă sau editează membrii echipei"
                icon={<PeopleIcon />}
                buttonText="Gestionează Staff"
                color="info"
                onClick={() => navigate('/shelter/staff')}
              />
            </Grid>
          </>
        )}
      </Grid>

      {/* Recent Activity */}
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Activitate Recentă
      </Typography>
      
      <Card>
        <CardContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Bun venit în sistem!</strong> Dashboard-ul pentru adaposturi este în dezvoltare. 
              Funcționalitățile complete vor fi disponibile în curând.
            </Typography>
          </Alert>
          
          <Stack spacing={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'success.light' }}>
                <PetsIcon />
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Adapostul a fost activat cu succes
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Acum poți începe să adaugi animale și să gestionezi adapostul
                </Typography>
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ShelterDashboard;
